import { ref, computed } from 'vue'
import type { EventInput } from '@fullcalendar/core'

export const LATE_NIGHT_START_HOUR = 20

/**
 * 日付をYYYY-MM-DD形式の文字列に変換
 */
export const formatDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Dateオブジェクトからオフセットなしのローカル時刻ISO文字列を生成
 * FullCalendarのtimeZone: 'local'設定ではオフセットなし文字列をローカル時刻として解釈する
 */
const formatLocalDateTime = (date: Date): string => {
  return `${formatDateString(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:00`
}

/**
 * ナイトプラン時間帯（20:00〜閉店）のバックグラウンドイベントを生成
 *
 * @param slotMaxTime - FullCalendarのslotMaxTime（例: "26:00:00"）
 * @param baseDate - 基準日（この日の前後にイベントを生成）
 * @param range - 基準日から前後何日分生成するか（デフォルト: 14）
 */
export const generateNightPlanEvents = (
  slotMaxTime: string,
  baseDate: Date,
  range = 14
): EventInput[] => {
  // slotMaxTime は "26:00:00" のような24時間超表記。分を捨てると
  // 閉店 02:30（= "26:30:00"）の営業日でも背景イベントが 02:00 までしか描画されず、
  // 20:30 閉店（= "20:30:00"）だと maxHour(20) <= 20 でイベント自体が消える（issue #331）。
  const [maxHourRaw, maxMinuteRaw] = slotMaxTime.split(':')
  const maxHour = parseInt(maxHourRaw, 10)
  const maxMinute = parseInt(maxMinuteRaw ?? '0', 10) || 0

  // ナイトプラン開始時刻以降が表示範囲にない場合はイベント不要
  if (maxHour * 60 + maxMinute <= LATE_NIGHT_START_HOUR * 60) return []

  const nightEndRealHour = maxHour >= 24 ? maxHour - 24 : maxHour

  const events: EventInput[] = []
  for (let dayOffset = -range; dayOffset <= range; dayOffset++) {
    const date = new Date(baseDate)
    date.setDate(date.getDate() + dayOffset)
    const dateStr = formatDateString(date)

    const startDate = new Date(date)
    startDate.setHours(LATE_NIGHT_START_HOUR, 0, 0, 0)

    const endDate = new Date(date)
    if (maxHour >= 24) {
      endDate.setDate(endDate.getDate() + 1)
    }
    endDate.setHours(nightEndRealHour, maxMinute, 0, 0)

    events.push({
      id: `night-plan-${dateStr}`,
      start: formatLocalDateTime(startDate),
      end: formatLocalDateTime(endDate),
      display: 'background',
      classNames: ['night-plan-zone']
    })
  }
  return events
}

/**
 * ナイトプラン時間帯のバックグラウンドイベントを提供するコンポーザブル
 *
 * FullCalendarのdatesSetコールバックで表示範囲が変わるたびにイベントを再生成する。
 * これにより±14日を超えるナビゲーションでも背景色が表示される。
 */
export const useNightPlanEvents = (slotMaxTime: string, initialBaseDate?: string) => {
  const baseDate = ref(
    initialBaseDate ? new Date(initialBaseDate + 'T00:00:00') : new Date()
  )

  const nightPlanEvents = computed(() =>
    generateNightPlanEvents(slotMaxTime, baseDate.value)
  )

  // FullCalendarのdatesSetから呼ぶハンドラ
  // 同一日付の場合はスキップして、datesSet→baseDate更新→calendarOptions再計算→
  // FullCalendar再レンダリング→datesSet再発火の無限ループを防止する
  const updateBaseDate = (newDate: Date) => {
    if (baseDate.value.getTime() === newDate.getTime()) return
    baseDate.value = newDate
  }

  return {
    nightPlanEvents,
    updateBaseDate
  }
}

/**
 * 深夜帯（slotMinHour未満 = 営業日の翌日側）の時刻を24時間加算して営業日の連続軸に載せる。
 *
 * バックエンドの `time.hour < ReservationService.slot_min_hour なら +1.day` と同じ基準。
 * この正規化を省くと 01:00-02:00 のような翌日側の時間帯が「早朝」と誤判定され、
 * ナイト帯判定・重複判定・料金表示がバックエンドと食い違う（issue #331）。
 * 時刻を分値で比較する箇所は必ずこの関数を通すこと。
 */
export const adjustHourForSlotMin = (hour: number, slotMinHour: number): number => {
  return hour < slotMinHour ? hour + 24 : hour
}

/**
 * "HH:MM" を営業日の連続軸上の分値に変換する（24時間超表記もそのまま扱える）
 */
export const timeStringToAdjustedMinutes = (timeStr: string, slotMinHour: number): number => {
  const [h, m] = timeStr.split(':').map(Number)
  return adjustHourForSlotMin(h, slotMinHour) * 60 + (m || 0)
}

/**
 * 時間がナイトプラン時間帯（20:00以降）かどうかを判定
 */
export const isNightPlanHour = (hour: number, slotMinHour: number): boolean => {
  return adjustHourForSlotMin(hour, slotMinHour) >= LATE_NIGHT_START_HOUR
}
